import { deletePushSubscriptionAction } from '@app-worker/shared/pushNotifications/deletePushSubscriptionAction';
import { DeletePushSubscriptionRequest } from '@repo/features/deletePushSubscriptionUseCase';

type ServiceWorkers = Pick<ServiceWorkerContainer, 'getRegistration'>;

/**
 * Sign-out's half of push: the phone stops being reachable for this worker,
 * so the next person signed in on it never gets his messages. The phone is
 * unsubscribed first, then the row goes, so a row never points at a phone
 * that dropped it.
 *
 * Best-effort, like the rest of `clearWorkerDevice`. `getRegistration`, not
 * `ready`, which never settles on a phone with no service worker. A failed
 * delete is dropped: the login page clears too, with no session to delete
 * with, and the next push to the dead endpoint prunes the row.
 */
export async function clearPushSubscription({
  findServiceWorkers = findBrowserServiceWorkers,
  deletePushSubscription = deletePushSubscriptionAction,
}: {
  findServiceWorkers?: () => ServiceWorkers | undefined;
  deletePushSubscription?: (
    request: DeletePushSubscriptionRequest,
  ) => Promise<void>;
} = {}): Promise<void> {
  try {
    const registration = await findServiceWorkers()?.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription === null || subscription === undefined) return;
    await subscription.unsubscribe();
    await deletePushSubscription({ endpoint: subscription.endpoint });
  } catch {
    // signing out goes on; see above
  }
}

// Undefined on an insecure origin or an old browser, whatever the DOM types say.
function findBrowserServiceWorkers(): ServiceWorkers | undefined {
  return navigator.serviceWorker;
}
